'use client';

import { useCallback, useLayoutEffect, useState } from 'react';
import type { Boundary, Placement, ReferenceElement } from '@floating-ui/dom';
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
  shift,
  size,
} from '@floating-ui/dom';

export type UseFloatingPositionProps = {
  /** Whether the positioning logic is enabled */
  enabled?: boolean;
  /** Offset in pixels along the main axis (e.g., vertical offset for top/bottom placements) */
  offsetMainAxis?: number;
  /** Offset in pixels along the cross axis (e.g., horizontal offset for top/bottom placements) */
  offsetCrossAxis?: number;
  /** Preferred placement of the tooltip relative to the target element */
  placement?: Placement;
  /** Reference to the arrow element for positioning calculations */
  arrowRef?: React.RefObject<HTMLElement | null>;
  /** Boundary element or area that the tooltip should stay within */
  boundary?: Boundary;
  /** Whether the tooltip should be hidden when the target element is hidden */
  autoHide?: boolean;
  /** Fallback placements to try if the preferred placement is not available */
  fallbackPlacements?: Placement[];
  /** Padding to add to the tooltip to account for overflow */
  overflowPadding?: number;
  /** Whether the tooltip should flip to the opposite side when it would overflow */
  enableFlip?: boolean;
  /** Whether to apply size constraints based on available space */
  applySizeConstraints?: boolean;
  /** Whether to match the width of the reference element */
  matchReferenceWidth?: boolean;
};

export function useFloatingPosition<T extends HTMLElement>(
  referenceElement?: ReferenceElement,
  floatingRef?: React.RefObject<T | null>,
  {
    enabled = true,
    offsetMainAxis = 0,
    offsetCrossAxis = 0,
    placement = 'top',
    arrowRef,
    boundary,
    autoHide = true,
    fallbackPlacements,
    overflowPadding = 8,
    enableFlip = true,
    applySizeConstraints = true,
    matchReferenceWidth = false,
  }: UseFloatingPositionProps = {}
) {
  const [resolvedPlacement, setResolvedPlacement] = useState<Placement>();
  const [referenceHidden, setReferenceHidden] = useState(false);

  const updatePosition = useCallback(async () => {
    if (!floatingRef?.current || !referenceElement) {
      return;
    }

    const floatingElement = floatingRef.current;
    const middlewareStack = [
      // Applies offset to the tooltip position along main and cross axes
      offset({ mainAxis: offsetMainAxis, crossAxis: offsetCrossAxis }),

      // Changes placement when tooltip would overflow the boundary
      // Falls back to alternative placements if the preferred one doesn't fit
      enableFlip &&
        flip({
          boundary,
          fallbackPlacements,
        }),

      // Shifts the tooltip to keep it within the boundary when it would overflow
      shift({ boundary, crossAxis: true, padding: overflowPadding }),

      // Hides the tooltip when the reference element is no longer visible
      autoHide && hide({ strategy: 'referenceHidden' }),

      // Positions the arrow element to point at the reference element
      // Only included if an arrow reference exists
      arrowRef?.current && arrow({ element: arrowRef.current, padding: 2 }),

      // Applies size constraints based on available space
      applySizeConstraints &&
        size({
          boundary,
          padding: overflowPadding,
          apply({ availableWidth, availableHeight, elements, rects }) {
            const floatingElement = elements.floating;

            // Apply max-height based on available space
            if (availableHeight >= floatingElement.scrollHeight) {
              floatingElement.style.maxHeight = '';
            } else {
              floatingElement.style.maxHeight = `${Math.max(
                0,
                availableHeight
              )}px`;
            }

            // Apply max-width based on available space
            if (availableWidth >= floatingElement.scrollWidth) {
              floatingElement.style.maxWidth = '';
            } else {
              floatingElement.style.maxWidth = `${Math.max(
                0,
                availableWidth
              )}px`;
            }

            // Match reference width if enabled
            if (matchReferenceWidth) {
              floatingElement.style.minWidth = `${rects.reference.width}px`;
            }
          },
        }),
    ].filter(Boolean); // Removes any falsy values from the middleware array

    const {
      x,
      y,
      middlewareData,
      placement: newPlacement,
    } = await computePosition(referenceElement, floatingElement, {
      placement,
      middleware: middlewareStack,
    });

    // If the placement is flipped, we need to reverse the offsetCrossAxis
    const isFlipped =
      (placement.includes('start') && newPlacement.includes('end')) ||
      (placement.includes('end') && newPlacement.includes('start'));

    // Update tooltip position and visibility
    const floatingStyles = {
      position: 'absolute',
      left: `${x}px`,
      top: isFlipped
        ? `${y + (offsetCrossAxis ?? 0) * -2 + 1}px`
        : `${y}px`,
    };

    Object.assign(floatingElement.style, floatingStyles);

    // Update placement state
    setResolvedPlacement(newPlacement);
    setReferenceHidden(!!middlewareData.hide?.referenceHidden);

    // Update arrow position if it exists
    if (arrowRef?.current && middlewareData.arrow) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow;
      const arrowStyles = {
        left: arrowX != null ? `${arrowX}px` : '',
        top: arrowY != null ? `${arrowY}px` : '',
      };
      Object.assign(arrowRef.current.style, arrowStyles);

      // Mark the arrow as positioned by floating-ui
      arrowRef.current.setAttribute('data-floating-ui-positioned', '');
    }
  }, [
    arrowRef,
    boundary,
    offsetMainAxis,
    offsetCrossAxis,
    placement,
    referenceElement,
    floatingRef,
    autoHide,
    fallbackPlacements,
    overflowPadding,
    enableFlip,
    applySizeConstraints,
    matchReferenceWidth,
  ]);

  useLayoutEffect(() => {
    if (enabled && floatingRef?.current && referenceElement) {
      return autoUpdate(referenceElement, floatingRef.current, updatePosition);
    }

    return undefined;
  }, [
    arrowRef,
    boundary,
    enabled,
    offsetMainAxis,
    offsetCrossAxis,
    placement,
    referenceElement,
    floatingRef,
    updatePosition,
    overflowPadding,
    enableFlip,
    applySizeConstraints,
    matchReferenceWidth,
  ]);

  return { placement: resolvedPlacement, referenceHidden, updatePosition };
}
